import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal, ExternalLink, Send, Trash2, Signal, Hash, GitBranch } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import ContextMenu from '../ui/ContextMenu';
import { PRIORITY_LEVELS, PriorityBarsIcon, priorityLabel } from './TaskPriorityBars';
import { taskKeyMenuItems } from './TaskKeys';

// The click-catching wrapper of a portalled menu takes no place in the row:
// as a real box it joined the row's flex gap and shifted the row by 10 px
// whenever the menu opened (owner, 13.10.2026).
const MENU_WRAP = { display: 'contents' };

/**
 * The card's ⋯ menu (Boards Redesign card 2.5). Replaces the three icon
 * buttons that used to sit in the card footer: the button shows on hover or
 * keyboard focus (TaskCard.css), and opens the same actions — Edit, Send to
 * chat (team boards only: `onShare` is null elsewhere), Delete — plus
 * Priority, because a bare medium-priority card has no signal to click
 * (taskCardMeta.js) and Trello / Linear keep every quick edit in this menu.
 *
 * Pointer-downs and clicks stop here: the card is a dnd-kit drag handle and
 * its title starts a rename on click. The menu is portalled, but React
 * bubbles its events through THIS tree, hence the wrapper around it too.
 */
const stop = (e) => e.stopPropagation();

export default function TaskCardMenu({ priority = 'medium', onOpenDetail, onShare = null, onDelete, onPriority, onOpenChange, contextAt = null, taskKey = null, title = '' }) {
  const ref = useRef(null);
  const [menu, setMenu] = useState(null);

  const setOpen = (next) => {
    setMenu(next);
    onOpenChange?.(!!next);
  };

  const open = (e) => {
    e.stopPropagation();
    const r = ref.current?.getBoundingClientRect();
    setOpen(r ? { x: r.right, y: r.bottom + 4, align: 'end' } : { x: e.clientX, y: e.clientY, align: 'end' });
  };

  // Right-click on the card opens this same menu at the pointer (owner,
  // 13.10.2026). TaskCard hands over { x, y, n }; `n` makes a second
  // right-click on the same spot reopen it.
  useEffect(() => {
    if (contextAt) setOpen({ x: contextAt.x, y: contextAt.y, align: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open on each new request only
  }, [contextAt]);

  const items = [
    // "Open", not "Edit" (owner, 13.10.2026): it opens the task window.
    { icon: <ExternalLink size={14} />, label: 'Open', onClick: () => onOpenDetail?.() },
    onShare && { icon: <Send size={14} />, label: 'Send to chat', onClick: () => onShare() },
    // Copy key / Copy branch name (commit ↔ task links): only when the card has a key.
    ...taskKeyMenuItems(taskKey, title, { key: <Hash size={14} />, branch: <GitBranch size={14} /> }),
    onPriority && {
      icon: <Signal size={14} />,
      label: 'Priority',
      items: PRIORITY_LEVELS.map((p) => ({
        icon: <PriorityBarsIcon priority={p} />,
        label: priorityLabel(p).replace(' priority', ''),
        checked: p === priority,
        onClick: () => {
          if (p !== priority) onPriority(p);
        },
      })),
    },
    { separator: true },
    { icon: <Trash2 size={14} />, label: 'Delete', danger: true, onClick: () => onDelete?.() },
  ];

  return (
    <>
      <Tooltip content="More actions" side="top">
        <button
          ref={ref}
          type="button"
          className="task-card-menu-btn"
          aria-label="Card menu"
          aria-haspopup="menu"
          aria-expanded={!!menu}
          onPointerDown={stop}
          onClick={open}
        >
          <MoreHorizontal size={13} />
        </button>
      </Tooltip>
      {menu && (
        <span style={MENU_WRAP} onClick={stop} onPointerDown={stop} onMouseDown={stop}>
          <ContextMenu
            x={menu.x}
            y={menu.y}
            align={menu.align || 'end'}
            className="task-card-menu"
            onClose={() => setOpen(null)}
            items={items}
          />
        </span>
      )}
    </>
  );
}
